import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LeadSubmitNetworkError, submitPublicLead } from '../services/publicLeadService';

type FormState = {
  fullName: string;
  email: string;
  companyName: string;
  phoneNumber: string;
  teamSize: string;
  notes: string;
};

const TEAM_SIZE_OPTIONS = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '500+',
];

const FEATURE_ITEMS = [
  {
    title: 'One-tap Booking',
    text: 'Seamless reservation across flights, rail, and boutique stays.',
    icon: '↘',
  },
  {
    title: 'AI Expense Audit',
    text: 'Instant compliance checks with neural receipt processing.',
    icon: '◉',
  },
  {
    title: 'Real-time Compliance',
    text: 'Dynamic policy enforcement at the moment of spend.',
    icon: '☑',
  },
];

const PublicLeadFormPage: React.FC = () => {
  const [form, setForm] = useState<FormState>({
    fullName: '',
    email: '',
    companyName: '',
    phoneNumber: '',
    teamSize: '',
    notes: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const title = 'Get Started with FlintPay';
  const subtitle ='Transform your travel management in under 15 minutes.'

  const handleInput = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    if (errorMessage) setErrorMessage(null);
    if (successMessage) setSuccessMessage(null);
  };

  const validateForm = (): string | null => {
    if (!form.fullName.trim()) return 'Full name is required';
    if (form.fullName.trim().length < 2) return 'Name must be at least 2 characters';

    const email = form.email.trim();
    if (!email) return 'Work email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid work email';

    if (!form.companyName.trim()) return 'Company name is required';
    if (!form.phoneNumber.trim()) return 'Phone number is required';
    if (!form.teamSize.trim()) return 'Company size is required';
    if (!form.notes.trim()) return 'Notes are required';
    return null;
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage(null);
      setSuccessMessage(null);

      await submitPublicLead({
        fullName: form.fullName.trim(),
        email: form.email.trim().toLowerCase(),
        companyName: form.companyName.trim(),
        phoneNumber: form.phoneNumber.trim(),
        teamSize: form.teamSize.trim(),
        notes: form.notes.trim(),
      });

      setSuccessMessage('Thanks, your details were submitted. Our team will contact you soon.');
      setForm({
        fullName: '',
        email: '',
        companyName: '',
        phoneNumber: '',
        teamSize: '',
        notes: '',
      });
    } catch (error) {
      if (error instanceof LeadSubmitNetworkError) {
        setErrorMessage(error.message);
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="font-ui-premium min-h-screen bg-[#ebf6fb] px-3 py-4 lg:h-screen lg:overflow-hidden lg:px-4 lg:py-4">
      <div className="mx-auto w-full max-w-none lg:h-full">
        <div className="grid gap-4 lg:h-full lg:grid-cols-[1.05fr_1.15fr]">
          <section className="flex flex-col rounded-[34px] bg-[#e8f4fa] p-8 lg:p-8">
            <h1 className="font-headline text-[44px] font-extrabold leading-[0.95] tracking-[-0.03em] text-[#085f79] lg:text-[44px]">
              Experience the Future
              <br />
              <span className="italic text-[#0a6f8a] mt-6">of Enterprise Travel</span>
            </h1>
            <p className="mt-1 max-w-[720px] text-[18px] leading-[1.45] text-[#4f7590] lg:text-[22px]">
              Save up to <span className="font-extrabold text-[#106e86]">30%</span> on travel and eliminate manual expense tracking with AI-powered automation.
            </p>

            <div className="mt-2 rounded-[30px] border border-[#cfe3ed] bg-[#dff0f7] p-4 shadow-[0_24px_45px_-30px_rgba(8,84,112,0.6)]">
              <div className="overflow-hidden rounded-[24px]">
                <img
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1400&q=70"
                  alt="Laptop showing analytics dashboard"
                  className="h-[240px] w-full object-cover lg:h-[320px]"
                />
              </div>
            </div>

            <div className="mt-2 grid gap-3 sm:grid-cols-3">
              {FEATURE_ITEMS.map((item) => (
                <div
                  key={item.title}
                  className="rounded-[20px] border border-[#d3e6ef] bg-[#e6f2f8] px-5 py-5"
                >
                  <h3 className="mt-3 text-[14px] font-semibold leading-[1.3] text-[#0f5f79] lg:text-[20px]">
                  {item.title}
                  </h3>
                  <p className="mt-2 text-[12px] leading-[1.5] text-[#5f8395] lg:text-[15px]">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="flex items-start lg:pt-1">
            {successMessage ? (
              <section className="flex min-h-[520px] w-full items-center justify-center">
                <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
                  <div className="text-4xl">✅</div>

                  <h2 className="mt-4 text-xl font-bold text-[#0b4f68]">
                    You&apos;re all set!
                  </h2>

                  <p className="mt-2 text-sm text-gray-600">
                    Our team will reach out within 24 hours.
                  </p>

                  <button
                    onClick={() => setSuccessMessage(null)}
                    className="mt-6 w-full rounded-lg bg-[#0788a9] py-3 text-sm font-semibold text-white"
                  >
                    Submit Another Request
                  </button>


                </div>
              </section>
            ) : (
              <div className="w-full max-w-3xl rounded-2xl bg-white p-7 shadow-lg mt-20">

              {/* HEADER */}
              <h2 className="text-2xl font-bold text-[#0b4f68] lg:text-3xl">
                {title}
              </h2>
              <p className="mt-1 text-sm text-[#2b7088]">
                {subtitle}
              </p>

              <form className="mt-5 space-y-3" onSubmit={onSubmit}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-semibold text-gray-500">
                      Full Name
                    </label>
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      required
                      value={form.fullName}
                      onChange={handleInput}
                      placeholder="John Doe"
                      className="mt-1 w-full rounded-lg border px-4 py-3 text-sm focus:border-[#0788a9] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-500">
                      Work Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleInput}
                      placeholder="john@company.com"
                      className="mt-1 w-full rounded-lg border px-4 py-3 text-sm focus:border-[#0788a9] focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-500">
                    Company Name
                  </label>
                  <input
                    id="companyName"
                    name="companyName"
                    type="text"
                    required
                    value={form.companyName}
                    onChange={handleInput}
                    placeholder="Acme Inc."
                    className="mt-1 w-full rounded-lg border px-4 py-3 text-sm focus:border-[#0788a9] focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-semibold text-gray-500">
                      Phone Number
                    </label>
                    <input
                      id="phoneNumber"
                      name="phoneNumber"
                      type="tel"
                      required
                      value={form.phoneNumber}
                      onChange={handleInput}
                      placeholder="+91 98765 43210"
                      className="mt-1 w-full rounded-lg border px-4 py-3 text-sm focus:border-[#0788a9] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-gray-500">
                      Company Size
                    </label>
                    <select
                      name="teamSize"
                      required
                      value={form.teamSize}
                      onChange={handleInput}
                      className="mt-1 w-full rounded-lg border px-4 py-3 text-sm"
                    >
                      <option value="">Select size...</option>
                      {TEAM_SIZE_OPTIONS.map((size) => (
                        <option key={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* NOTES */}
                <div>
                  <label className="text-sm font-semibold text-gray-500">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    required
                    value={form.notes}
                    onChange={handleInput}
                    rows={3}
                    placeholder="Tell us your needs..."
                    className="mt-1 w-full rounded-lg border px-4 py-3 text-sm focus:border-[#0788a9] focus:outline-none"
                  />
                </div>

                {/* ERROR / SUCCESS */}
                {errorMessage && (
                  <p className="text-sm text-red-600">{errorMessage}</p>
                )}

                {/* CTA */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-lg bg-[#0788a9] py-3 text-sm font-semibold text-white hover:bg-[#067895]"
                >
                  {isLoading
                    ? "Submitting..."
                    : "Get Started"}
                </button>

                {/* TRUST */}
                <p className="text-center text-sm text-gray-400">
                  Trusted by modern finance teams
                </p>

                {/* BACK */}
                <div className="flex justify-center">
                  <Link
                    to="/"
                    className="text-sm text-[#0788a9] hover:underline"
                  >
                    Back to Home
                  </Link>
                </div>
              </form>
            </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default PublicLeadFormPage;
